import { Brand, type entities, LogLevel, parseStage, utilities } from "@fraction/shared";
import { CURRENT_BRAND } from "src/hooks/useBrand";

const STAGE: "dev" | "prod" | "local" | undefined = parseStage(
  import.meta.env.VITE_STAGE || import.meta.env.STORYBOOK_APP_STAGE
);

const IS_PROD = STAGE === "prod";
const IS_LOCAL = STAGE === "local";
const BROWSER_DOMAIN = IS_PROD ? CURRENT_BRAND.cookieDomain : document.location.host?.replace("https://", "");
const BROWSER_HOST = BROWSER_DOMAIN.split(":")[0];

const config = {
  anonymizeApplicantDetails: IS_PROD
    ? CURRENT_BRAND.key === Brand.COLDWELL
    : CURRENT_BRAND.key === Brand.COLDWELL,
  isProd: IS_PROD,
  isLocal: IS_LOCAL,
  /**
   * Whether onboarding is the Newton Velocity connect flow.
   *
   * Grid and Coldwell onboard by importing a broker's existing Newton deals, so the lender-matching
   * and cleanup those brands sell has something to work on from day one. Fraction takes deals
   * through the broker channels instead, so its brokers get the broker-setup flow (brokerage, deal
   * team, how to submit) and are never asked for an API key to an integration they do not use.
   *
   * Listed as an explicit set rather than `!== FRACTION` on purpose: a brand added later should
   * fall to the broker-setup flow, which asks nothing integration-specific, rather than being sent
   * to connect a Newton account it may not have.
   *
   * Every brand DOES onboard, so /onboarding stays the post-signup target for all of them; only the
   * body of the page differs.
   */
  usesNewtonOnboarding: CURRENT_BRAND.key === Brand.GRID || CURRENT_BRAND.key === Brand.COLDWELL,
  stage: STAGE, // local, dev, prod
  logLevel: utilities.number.parseInt(import.meta.env.VITE_LOG_LEVEL, LogLevel.LOG),
  brandName: CURRENT_BRAND.name,
  brandAppURL: CURRENT_BRAND.appUrl,
  brandPublicURL: CURRENT_BRAND.publicUrl,
  brandSupportEmail: CURRENT_BRAND.supportEmail,
  brandSupportPhone: CURRENT_BRAND.supportPhone,
  brandContactEmail: CURRENT_BRAND.contactEmail,
  brandEmailDomains: CURRENT_BRAND.emailDomains,
  brandFaviconHref: CURRENT_BRAND.faviconHref,
  baseURL: IS_LOCAL
    ? `http://${BROWSER_HOST}:9000`
    : IS_PROD
      ? "https://api.fraction.com"
      : "https://dev.fraction.com",
  prodBaseURL: "https://api.fraction.com",
  frontendBaseURL: IS_LOCAL
    ? "http://localhost:3000"
    : IS_PROD
      ? CURRENT_BRAND.appUrl
      : "https://app-dev-fraction-com--fraction.netlify.app",
  segmentWriteKey: IS_PROD ? "Z68ZA33fqhf2k8XHDeUBBCFdaYuCAQ3Y" : "NPZJ98kojGCoTTcPpbJs5JUFFKJfaD49",
  datadogClientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN || "NO_TOKEN",
  launchDarklyClientID: IS_PROD ? "62058981f91f0011fdab6844" : "62058981f91f0011fdab6843",
  googlePlacesAPIKey:
    import.meta.env.VITE_GOOGLE_PLACES_API_KEY || import.meta.env.STORYBOOK_GOOGLE_PLACES_API_KEY,
  s3URL: {
    publicMedia: "https://fraction-public-media.s3.ca-central-1.amazonaws.com",
    legalDocs: "https://fraction-legal-docs.s3.us-west-1.amazonaws.com",
  },
  browserDomain: BROWSER_DOMAIN,
  // if you set ANYTHING for localhost for cookie domain, nothing will get saved.
  cookieDomain: IS_LOCAL ? undefined : BROWSER_DOMAIN,
  websocketURL: IS_LOCAL
    ? "ws://localhost:9000"
    : IS_PROD
      ? "wss://ws.api.fraction.com"
      : "wss://ws.dev.fraction.com",
};

export const docApprovalLink = (applicationId?: string, fileId?: string) =>
  applicationId
    ? `${config.frontendBaseURL}/app/${applicationId}?doc=${fileId}`
    : `${config.frontendBaseURL}/queue?fileId=${fileId}`;

export const constructLink = (link?: entities.LinkT): typeof link extends undefined ? undefined : string => {
  if (link === undefined) {
    // @ts-expect-error
    return undefined;
  }
  return `${config.baseURL}/application/link/${link.eid}`;
};
export default config;
